import { useEffect } from "react";
import type { RefObject } from "react";

/** A blur this soon before the page went away was the page going away. */
const LEAVING_MS = 400;

/**
 * Give a field its focus back when the visitor returns to the page: they
 * left for their mail app to read the code, the phone dropped the keyboard,
 * and they should not have to tap the field to get it back. Some in-app
 * browsers only raise the keyboard for a tap; there this still restores the
 * caret, and nothing else changes. The browser is left to scroll the field
 * into view: on a phone that scroll is what lifts a sheet pinned to the
 * bottom of the page clear of the keyboard.
 *
 * Only an interrupted entry is resumed. A visitor who moved on from the
 * field themselves (a tap elsewhere, the resend link) keeps the focus where
 * they put it.
 */
export function useRefocusOnReturn(
  inputRef: RefObject<HTMLInputElement | null>,
  active = true,
) {
  useEffect(() => {
    const input = inputRef.current;
    if (!active || !input) return;
    // When the field last lost focus while the page was in front, and
    // whether leaving the page is what took the field's focus.
    let blurredAt: number | null = null;
    let interrupted = false;
    const onBlur = () => {
      blurredAt = Date.now();
    };
    const onFocus = () => {
      blurredAt = null;
      interrupted = false;
    };
    const leave = () => {
      if (interrupted) return;
      interrupted =
        document.activeElement === input ||
        (blurredAt !== null && Date.now() - blurredAt < LEAVING_MS);
    };
    const comeBack = () => {
      if (document.visibilityState !== "visible") return leave();
      if (!interrupted) return;
      interrupted = false;
      input.focus();
    };
    input.addEventListener("blur", onBlur);
    input.addEventListener("focus", onFocus);
    window.addEventListener("blur", leave);
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", comeBack);
    window.addEventListener("focus", comeBack);
    window.addEventListener("pageshow", comeBack);
    return () => {
      input.removeEventListener("blur", onBlur);
      input.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", leave);
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", comeBack);
      window.removeEventListener("focus", comeBack);
      window.removeEventListener("pageshow", comeBack);
    };
  }, [inputRef, active]);
}
